import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Drawer,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Divider, CircularProgress, Alert, InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { apiFetch } from '../api';

// IMAGE_BASE_URL — env मधिल port वापरतो (लोकल dev: 8000, production: no port)
const _imgProtocol = process.env.REACT_APP_API_PROTOCOL || 'http';
const _imgPort = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';
const IMAGE_BASE_URL = `${_imgProtocol}://${window.location.hostname}${_imgPort}/storage`;

const CustomerMaster = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    address: '',
    opening_balance: 0,
    status: 'Active',
    photo: null,       // File object
    photo_path: null   // String from server
  });

  const fetchData = async () => {
    setLoading(true);
    try {
        const response = await apiFetch('/api/customers');
        const data = await response.json();
        if (Array.isArray(data)) {
            setCustomers(data);
        } else {
            console.error("API Error Response:", data);
            setError(data.error || 'डेटा लोड करताना त्रुटी आली.');
            setCustomers([]);
        }
    } catch (err) {
        console.error(err);
        setError('डेटा लोड करताना त्रुटी आली.');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDrawerForCreate = () => {
    setEditingId(null);
    setFormData({ 
      name: '', 
      mobile: '', 
      address: '', 
      opening_balance: 0, 
      status: 'Active',
      photo: null,
      photo_path: null
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      name: row.name,
      mobile: row.mobile || '',
      address: row.address || '',
      opening_balance: row.opening_balance || 0,
      status: row.status || 'Active',
      photo: null,
      photo_path: row.photo_path || null
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('तुम्हाला नक्की हे ग्राहक डिलीट करायचे आहे का?')) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/customers/${id}`, { method: 'DELETE' });
      if (response.ok) {
        fetchData();
      } else {
        setError('डिलीट करताना अडचण आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
        setError('कृपया ग्राहकाचे नाव भरा.');
        return;
    }

    setLoading(true);
    const isEditing = !!editingId;
    const endpoint = isEditing ? `/api/customers/${editingId}` : '/api/customers';
    const method = isEditing ? 'PUT' : 'POST';

    try {
        const data = new FormData();
        data.append('name', formData.name);
        data.append('mobile', formData.mobile);
        data.append('address', formData.address);
        data.append('opening_balance', formData.opening_balance);
        data.append('status', formData.status);
        
        if (formData.photo) {
            data.append('photo', formData.photo);
        }

        if (isEditing) {
            data.append('_method', 'PUT');
        }

        const response = await apiFetch(endpoint, {
            method: 'POST', // Use POST for both to support FormData upload
            body: data
        });

        if (response.ok) {
            setDrawerOpen(false);
            setEditingId(null);
            setFormData({ name: '', mobile: '', address: '', opening_balance: 0, status: 'Active', photo: null, photo_path: null });
            fetchData();
        } else {
            setError('ग्राहक जतन करताना अडचण आली.');
        }
    } catch (err) {
        setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
        setLoading(false);
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case 'Active': return 'सक्रिय';
      case 'Inactive': return 'निष्क्रिय';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
          ग्राहक मास्टर
        </Typography>
        <Button
          variant="contained"
          onClick={openDrawerForCreate}
          sx={{ backgroundColor: '#0FA958', '&:hover': { backgroundColor: '#0C8A47' }, minWidth: 200 }}
        >
          {loading && !drawerOpen ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : '+ नवीन ग्राहक जोडा'}
        </Button>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ display: 'flex', mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="ग्राहकाचे नाव शोधा..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#627D98' }} />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      {error && !drawerOpen && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 10px rgba(0,0,0,0.05)', maxHeight: '70vh', overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 650 }} aria-label="customer table">
          <TableHead sx={{ backgroundColor: '#f0fdf4' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>अ. क्र.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>फोटो</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ग्राहकाचे नाव</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>मोबाईल नंबर</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>पत्ता</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>सध्याची शिल्लक (Current) ₹</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>स्थिती</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>अॅक्शन</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && customers.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : customers
                .filter(row => row.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((row, index) => (
              <TableRow
                key={row.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#fcfcfc' } }}
              >
                <TableCell component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell>
                  <Box
                    component="img"
                    src={row.photo_path ? `${IMAGE_BASE_URL}/${row.photo_path}` : 'https://via.placeholder.com/40'}
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/40'; }}
                    sx={{ width: 45, height: 45, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{row.name}</Typography>
                </TableCell>
                <TableCell>{row.mobile}</TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell align="right" sx={{ color: row.current_balance > 0 ? '#d32f2f' : 'inherit', fontWeight: row.current_balance > 0 ? 'bold' : 'normal' }}>
                  ₹ {row.current_balance}
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      px: 2, py: 0.5,
                      borderRadius: 4,
                      display: 'inline-block',
                      backgroundColor: row.status === 'Active' ? '#e6f4ea' : '#f5f5f5',
                      color: row.status === 'Active' ? '#137333' : '#616161',
                      fontSize: '0.85rem'
                    }}
                  >
                    {translateStatus(row.status)}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#1B5E20' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: '#d32f2f' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && customers.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">अजून कोणतेही ग्राहक भरले नाहीत.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer Form */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
              {editingId ? 'ग्राहक संपादित करा' : 'नवीन ग्राहक जोडा'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {error && drawerOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Photo Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
                component="img"
                src={formData.photo ? URL.createObjectURL(formData.photo) : (formData.photo_path ? `${IMAGE_BASE_URL}/${formData.photo_path}` : 'https://via.placeholder.com/100')}
                sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '2px solid #0FA958', mb: 1 }}
            />
            <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCameraIcon />}
                size="small"
                sx={{ color: '#0FA958', borderColor: '#0FA958' }}
            >
                फोटो निवडा
                <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, photo: e.target.files[0] })}
                />
            </Button>
          </Box>
          <TextField
            fullWidth
            label="ग्राहकाचे नाव"
            placeholder="उदा. सचिन पाटील"
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            sx={{ mb: 2.5 }}
          />
          <TextField
            fullWidth
            label="मोबाईल नंबर"
            type="tel"
            placeholder="उदा. 9876543210"
            variant="outlined"
            value={formData.mobile}
            onChange={(e) => setFormData({...formData, mobile: e.target.value})}
            sx={{ mb: 2.5 }}
          />
          <TextField
            fullWidth
            label="पत्ता"
            placeholder="उदा. शिवाजी नगर, पुणे"
            variant="outlined"
            multiline
            rows={2}
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            sx={{ mb: 2.5 }}
          />
          <TextField
            fullWidth
            label="आरंभीची शिल्लक (Opening Balance) ₹"
            type="number"
            variant="outlined"
            value={formData.opening_balance}
            onChange={(e) => setFormData({...formData, opening_balance: e.target.value})}
            sx={{ mb: 2.5 }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>स्थिती</InputLabel>
            <Select 
                label="स्थिती" 
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <MenuItem value="Active">सक्रिय</MenuItem>
              <MenuItem value="Inactive">निष्क्रिय</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 'auto', display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setDrawerOpen(false)}
              color="inherit"
              sx={{ fontWeight: 'bold' }}
            >
              रद्द करा
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{ backgroundColor: '#0FA958', '&:hover': { backgroundColor: '#0C8A47' }, fontWeight: 'bold' }}
            >
               {loading ? <CircularProgress size={24} color="inherit" /> : 'जतन करा'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default CustomerMaster;
